import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { KeyRound, Loader2, ShieldCheck, MonitorSmartphone, LogOut, X, Globe2, ShieldOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Profile → Security: Google users set a password (email fallback when the OAuth redirect is blocked); others change theirs. */
export function PasswordSecurityCard({ user, onChanged }) {
  const hasPassword = !!user?.has_password;
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = pw.length >= 10 && /\d/.test(pw) && /[A-Za-z]/.test(pw) && pw === confirm && (!hasPassword || current.length > 0);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/set-password`, { new_password: pw, current_password: hasPassword ? current : null }, { withCredentials: true });
      toast.success(r.data.first_time ? 'Password set — you can now sign in with email and password' : 'Password updated');
      setCurrent(''); setPw(''); setConfirm('');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not set password'); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="profile-security-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#4ABFCF]" /> Security · {hasPassword ? 'Change password' : 'Set a password'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasPassword ? (
          <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded px-2.5 py-2" data-testid="profile-security-google-note">
            You sign in with Google. Setting a password lets you use <b>email + password</b> on networks that block the Google sign-in redirect. Google sign-in keeps working too, and either way you stay signed in for up to 30 days.
          </p>
        ) : (
          <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="profile-security-has-password"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Email + password sign-in is enabled for this account. Sessions last up to 30 days.</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {hasPassword && (
            <div>
              <Label className="text-xs text-slate-500">Current password</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 text-sm" autoComplete="current-password" data-testid="profile-current-password" />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">New password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 text-sm" autoComplete="new-password" data-testid="profile-new-password" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Confirm</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 text-sm" autoComplete="new-password" data-testid="profile-confirm-password" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">At least 10 characters with a letter and a number{pw && confirm && pw !== confirm ? <span className="text-rose-600"> · passwords do not match</span> : ''}.</p>
          <Button size="sm" onClick={submit} disabled={busy || !valid} className="bg-[#0F2040] hover:bg-[#0F2040]/90" data-testid="profile-set-password-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : hasPassword ? 'Update password' : 'Set password'}
          </Button>
        </div>
        <SessionsList />
        <DevicesAndLocations />
      </CardContent>
    </Card>
  );
}


const timeAgo = (iso) => {
  if (!iso) return '—';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

/** Active sessions across devices, with per-device sign-out and "sign out everywhere". */
function SessionsList() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API_URL}/api/auth/sessions`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ sessions: [], error: true }));
  useEffect(() => { load(); }, []);
  const revoke = async (s) => {
    setBusy(s.id);
    try {
      await axios.delete(`${API_URL}/api/auth/sessions/${encodeURIComponent(s.id)}`, { withCredentials: true });
      if (s.current) { sessionStorage.clear(); window.location.href = '/login'; return; }
      toast.success('Device signed out'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not sign out that device'); } finally { setBusy(null); }
  };
  const revokeAll = async (keepCurrent) => {
    setBusy('all');
    try {
      const r = await axios.post(`${API_URL}/api/auth/sessions/revoke-all`, { keep_current: keepCurrent }, { withCredentials: true });
      if (!keepCurrent) { sessionStorage.clear(); window.location.href = '/login'; return; }
      toast.success(`Signed out ${r.data.removed} other device${r.data.removed === 1 ? '' : 's'}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not sign out everywhere'); } finally { setBusy(null); }
  };
  const sessions = data?.sessions || [];
  return (
    <div className="pt-3 border-t border-slate-100" data-testid="profile-sessions">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <MonitorSmartphone className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Where you're signed in</span>
        <span className="text-xs text-slate-400">· {data ? `${sessions.length} active session${sessions.length === 1 ? '' : 's'}` : '…'}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === 'all' || sessions.length < 2} onClick={() => revokeAll(true)} data-testid="profile-signout-others-btn">
            <LogOut className="w-3 h-3 mr-1" /> Sign out other devices
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busy === 'all'} onClick={() => revokeAll(false)} data-testid="profile-signout-everywhere-btn">
            {busy === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><LogOut className="w-3 h-3 mr-1" /> Sign out everywhere</>}
          </Button>
        </div>
      </div>
      {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      {data?.error && <p className="text-xs text-rose-600">Could not load sessions.</p>}
      {sessions.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="profile-sessions-list">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`profile-session-${s.id.replace(':', '-')}`}>
              <div className="min-w-0">
                <p className="font-medium text-[#0F2040] truncate">{s.device} <span className="font-normal text-slate-400">· {s.kind === 'google' ? 'Google sign-in' : 'Email + password'}</span>
                  {s.current && <span className="ml-1.5 text-[10px] px-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="profile-session-current">This device</span>}
                </p>
                <p className="text-slate-500">{s.ip ? `${s.ip} · ` : ''}active {timeAgo(s.last_seen)} · signed in {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'} · expires {s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '—'}</p>
              </div>
              <button type="button" onClick={() => revoke(s)} disabled={busy === s.id} className="text-slate-400 hover:text-rose-600 shrink-0" title={s.current ? 'Sign out this device' : 'Sign out that device'} data-testid={`profile-session-revoke-${s.id.replace(':', '-')}`}>
                {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/** Known devices (trust toggle) and sign-in locations by country from recorded IPs. */
function DevicesAndLocations() {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API_URL}/api/auth/devices`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ devices: [], locations: [] }));
  useEffect(() => { load(); }, []);
  const setTrust = async (d, trusted) => {
    try {
      await axios.put(`${API_URL}/api/auth/devices/${d.id}/trust`, { trusted }, { withCredentials: true });
      toast.success(trusted ? `${d.device} is now trusted — no more alerts from it` : `${d.device} is no longer trusted`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update device'); }
  };
  if (!data) return null;
  const max = data.locations[0]?.sign_ins || 1;
  return (
    <div className="pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-devices">
      <div>
        <div className="flex items-center gap-2 mb-1.5"><Globe2 className="w-4 h-4 text-[#4ABFCF]" /><span className="text-sm font-semibold text-[#0F2040]">Sign-in locations</span><span className="text-xs text-slate-400">· by country, from recorded IPs</span></div>
        {data.locations.length === 0 && <p className="text-xs text-slate-400" data-testid="profile-locations-empty">No sign-ins recorded yet.</p>}
        <ul className="space-y-1" data-testid="profile-locations">
          {data.locations.map((l) => (
            <li key={l.country} className="text-xs" data-testid={`profile-location-${(l.country_code || l.country).toLowerCase()}`}>
              <div className="flex items-center justify-between"><span className="font-medium text-[#0F2040]">{l.country_code ? <span className="font-mono text-[10px] px-1 rounded bg-slate-100 mr-1.5">{l.country_code}</span> : null}{l.country}</span><span className="text-slate-500 tabular-nums">{l.sign_ins} device{l.sign_ins === 1 ? '' : 's'} · {l.ips} IP{l.ips === 1 ? '' : 's'} · {timeAgo(l.last_seen)}</span></div>
              <div className="h-1 rounded bg-slate-100 mt-0.5"><div className="h-1 rounded bg-[#4ABFCF]" style={{ width: `${Math.round((l.sign_ins / max) * 100)}%` }} /></div>
            </li>
          ))}
        </ul>
        <p className="text-[10px] text-slate-400 mt-1.5">Private or unresolvable addresses show as Unknown.</p>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5"><ShieldCheck className="w-4 h-4 text-[#4ABFCF]" /><span className="text-sm font-semibold text-[#0F2040]">Known devices</span><span className="text-xs text-slate-400">· trusted devices never trigger alerts</span></div>
        {data.devices.length === 0 && <p className="text-xs text-slate-400">No devices recorded yet.</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-44 overflow-y-auto" data-testid="profile-known-devices">
          {data.devices.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs" data-testid={`profile-device-${d.id}`}>
              <div className="min-w-0">
                <p className="font-medium text-[#0F2040] truncate">{d.device}{d.trusted && <span className="ml-1.5 text-[10px] px-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid={`profile-device-trusted-${d.id}`}>Trusted</span>}</p>
                <p className="text-slate-500 truncate">{d.ip}{d.city || d.country ? ` · ${[d.city, d.country].filter(Boolean).join(', ')}` : ''} · last {timeAgo(d.last_seen)}</p>
              </div>
              <button type="button" onClick={() => setTrust(d, !d.trusted)} className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-1.5 py-0.5 ${d.trusted ? 'text-slate-500 border-slate-200 hover:bg-slate-50' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`} data-testid={`profile-device-trust-${d.id}`}>
                {d.trusted ? <><ShieldOff className="w-3 h-3" /> Untrust</> : <><ShieldCheck className="w-3 h-3" /> Trust</>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
